import { API_BASE } from "../utils/apiBase";

const BASE = API_BASE;
const SCAN_UPLOAD_TARGET_BYTES = 1600 * 1024;
const SCAN_UPLOAD_MAX_EDGE = 1800;

function queryString(params = {}) {
  const search = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") search.set(key, value);
  });
  const text = search.toString();
  return text ? `?${text}` : "";
}

function loadImageFile(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Could not read image."));
    };
    img.src = url;
  });
}

function canvasToBlob(canvas, type, quality) {
  return new Promise(resolve => canvas.toBlob(resolve, type, quality));
}

async function prepareScanUpload(file, options = {}) {
  const targetBytes = options.targetBytes || SCAN_UPLOAD_TARGET_BYTES;
  const maxEdge = options.maxEdge || SCAN_UPLOAD_MAX_EDGE;
  const outputType = options.outputType || "image/jpeg";
  if (!file || !file.type?.startsWith("image/") || file.size <= targetBytes) return file;
  try {
    const img = await loadImageFile(file);
    const scale = Math.min(1, maxEdge / Math.max(img.width, img.height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(img.width * scale));
    canvas.height = Math.max(1, Math.round(img.height * scale));
    const ctx = canvas.getContext("2d");
    if (!ctx) return file;
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

    let bestBlob = null;
    for (const quality of [0.82, 0.72, 0.64, 0.56]) {
      const blob = await canvasToBlob(canvas, outputType, quality);
      if (!blob) continue;
      bestBlob = blob;
      if (blob.size <= targetBytes) break;
    }
    if (!bestBlob || bestBlob.size >= file.size) return file;
    const baseName = file.name?.replace(/\.[^.]+$/, "") || "scan-upload";
    const extension = outputType === "image/webp" ? "webp" : "jpg";
    return new File([bestBlob], `${baseName}.${extension}`, { type: outputType, lastModified: Date.now() });
  } catch {
    return file;
  }
}

function scanUploadErrorMessage(res) {
  return res.status === 413
    ? "Photo is too large. Try a clearer, closer photo or upload a smaller image."
    : `HTTP ${res.status}`;
}

export function getAuthToken() {
  return sessionStorage.getItem("hm_access_token") || localStorage.getItem("hm_access_token");
}

export function setPersistentAuthTokens(accessToken, refreshToken) {
  sessionStorage.removeItem("hm_access_token");
  sessionStorage.removeItem("hm_refresh_token");
  sessionStorage.removeItem("hm_impersonation_session");
  localStorage.setItem("hm_access_token", accessToken);
  if (refreshToken) localStorage.setItem("hm_refresh_token", refreshToken);
  else localStorage.removeItem("hm_refresh_token");
}

export function setImpersonationAuthToken(accessToken) {
  sessionStorage.setItem("hm_access_token", accessToken);
  sessionStorage.removeItem("hm_refresh_token");
  sessionStorage.setItem("hm_impersonation_session", "true");
}

export function clearAuthTokens() {
  const hasSessionToken = Boolean(sessionStorage.getItem("hm_access_token") || sessionStorage.getItem("hm_impersonation_session"));
  sessionStorage.removeItem("hm_access_token");
  sessionStorage.removeItem("hm_refresh_token");
  sessionStorage.removeItem("hm_impersonation_session");
  if (!hasSessionToken) {
    localStorage.removeItem("hm_access_token");
    localStorage.removeItem("hm_refresh_token");
  }
}

async function request(method, path, body) {
  const headers = { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest" };
  const token = getAuthToken();
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const res = await fetch(`${BASE}${path}`, {
    method,
    headers,
    body: body != null ? JSON.stringify(body) : undefined,
    // HouseMind API responses are live household state. A write may happen
    // through a different endpoint (for example a meal shortage adding to
    // Shopping), so the browser cannot safely reuse an earlier GET response.
    cache: method === "GET" ? "no-store" : "default",
  });

  if (res.status === 401) {
    clearAuthTokens();
    window.dispatchEvent(new Event("hm_signout"));
    let msg = "Session expired. Please sign in again.";
    try { msg = (await res.json()).error || msg; } catch {}
    const err = new Error(msg);
    err.status = res.status;
    throw err;
  }

  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    let data = null;
    try {
      data = await res.json();
      msg = data.error || msg;
    } catch {}
    const err = new Error(msg);
    err.status = res.status;
    err.data = data;
    err.hint = data?.hint;
    throw err;
  }

  if (res.status === 204) return null;
  return res.json();
}

export const authApi = {
  register: (data) => request("POST", "/auth/register", data),
  login: (email, password) => request("POST", "/auth/login", { email, password }),
  forgotPassword: (data) => request("POST", "/auth/forgot-password", data),
  resetPassword: (data) => request("POST", "/auth/reset-password", data),
  me: () => request("GET", "/auth/me"),
  updateMe: (data) => request("PATCH", "/auth/me", data),
  usage: () => request("GET", "/auth/me/usage"),
  googleAuth: (credential, options = {}) => request("POST", "/auth/google", {
    credential,
    age_confirmed: Boolean(options.age_confirmed),
  }),
};

export function logout() {
  clearAuthTokens();
  window.dispatchEvent(new Event("hm_signout"));
}

export const pantryApi = {
  list: () => request("GET", "/pantry/"),
  add: (data) => request("POST", "/pantry/", data),
  bulkAdd: (items) => request("POST", "/pantry/bulk-add", { items }),
  update: (id, data) => request("PATCH", `/pantry/${id}`, data),
  remove: (id) => request("DELETE", `/pantry/${id}`),
  barcode: (barcode) => request("POST", "/pantry/barcode", { barcode }),
  scan: async (file, scanType = "receipt") => {
    const uploadFile = await prepareScanUpload(file);
    const form = new FormData();
    form.append("file", uploadFile);
    form.append("scan_type", scanType);
    const token = getAuthToken();
    const headers = { "X-Requested-With": "XMLHttpRequest" };
    if (token) headers["Authorization"] = `Bearer ${token}`;
    return fetch(`${BASE}/pantry/scan`, { method: "POST", headers, body: form })
      .then(async res => {
        if (res.status === 401) {
          clearAuthTokens();
          window.dispatchEvent(new Event("hm_signout"));
          throw new Error("Session expired. Please sign in again.");
        }
        if (!res.ok) {
          let msg = scanUploadErrorMessage(res);
          try {
            const payload = await res.json();
            msg = payload.message || payload.error || msg;
          } catch {}
          throw new Error(msg);
        }
        return res.json();
      });
  },
};

export const mealsApi = {
  // Meal plans
  current: () => request("GET", "/meals/current"),
  list: () => request("GET", "/meals/"),
  create: (data) => request("POST", "/meals/", data),
  update: (id, data) => request("PATCH", `/meals/${id}`, data),
  remove: (id, mealType = "") => request("DELETE", `/meals/${id}${mealType ? `?meal_type=${encodeURIComponent(mealType)}` : ""}`),
  // AI prompts
  generate: (data = {}) => request("POST", "/meals/generate", data),
  regenerateDay: (planId, dayIndex, data) => request("POST", `/meals/${planId}/days/${dayIndex}/regenerate`, data),
  generateShoppingList: (planId) => request("POST", `/meals/${planId}/shopping`),
  shoppingListStatus: (planId) => request("GET", `/meals/shopping-lists/for-plan/${planId}`),
  suggestRecipes: (data) => request("POST", "/meals/recipes/suggest", data),
  savedRecipes: () => request("GET", "/meals/recipes"),
  saveRecipe: (data) => request("POST", "/meals/recipes", data),
  removeRecipe: (id) => request("DELETE", `/meals/recipes/${id}`),
  cookRecipe: (data) => request("POST", "/meals/recipes/cook", data),
  cookedRecipes: () => request("GET", "/meals/recipes/cooked"),
  expiryMeals: (data) => request("POST", "/meals/expiry-meals", data),
  deliveryLogs: (days = 30) => request("GET", `/meals/delivery-logs?days=${days}`),
  addDeliveryLog: (data) => request("POST", "/meals/delivery-logs", data),
  removeDeliveryLog: (id) => request("DELETE", `/meals/delivery-logs/${id}`),
  // Generation jobs — async meal plan generation
  createGenerationJob: (data) => request("POST", "/meals/generation-jobs", data),
  getGenerationJob: (jobId) => request("GET", `/meals/generation-jobs/${jobId}`),
  retryGenerationJob: (jobId) => request("POST", `/meals/generation-jobs/${jobId}/retry`),
  // Generic AI jobs (shopping list, day regenerate, etc.)
  createAiJob: (data) => request("POST", "/ai-jobs", data),
  getAiJob: (jobId) => request("GET", `/ai-jobs/${jobId}`),
  retryAiJob: (jobId) => request("POST", `/ai-jobs/${jobId}/retry`),
  // Meal image (Unsplash) — returns { image_url }
  mealImage: (name, mealType = "dinner", refresh = false) => request("GET", `/meals/meal-image?name=${encodeURIComponent(name)}&meal_type=${encodeURIComponent(mealType || "dinner")}${refresh ? "&refresh=true" : ""}`),
  // Preferences
  preferences: () => request("GET", "/meals/preferences"),
  savePreferences: (data) => request("POST", "/meals/preferences", data),
  // Structured shopping lists
  shoppingLists: () => request("GET", "/meals/shopping-lists"),
  getShoppingList: (id) => request("GET", `/meals/shopping-lists/${id}`),
  addShoppingItem: (listId, data) => request("POST", `/meals/shopping-lists/${listId}/items`, data),
  toggleShoppingItem: (listId, itemId, data) => request("PATCH", `/meals/shopping-lists/${listId}/items/${itemId}`, data),
  removeShoppingItem: (listId, itemId) => request("DELETE", `/meals/shopping-lists/${listId}/items/${itemId}`),
  clearCheckedShoppingItems: (listId) => request("POST", `/meals/shopping-lists/${listId}/clear-checked`),
  exportShoppingList: (listId) => request("POST", `/meals/shopping-lists/${listId}/export`),
  completeShoppingList: (listId, data = {}) => request("POST", `/meals/shopping-lists/${listId}/complete`, data),
};

export const aiJobsApi = {
  create: (data) => request("POST", "/ai-jobs", data),
  get: (jobId) => request("GET", `/ai-jobs/${jobId}`),
  retry: (jobId) => request("POST", `/ai-jobs/${jobId}/retry`),
};

export const shoppingApi = {
  list: () => request("GET", "/shopping/"),
  add: (data) => request("POST", "/shopping/", data),
  bulkAdd: (items) => request("POST", "/shopping/bulk", { items }),
  update: (id, data) => request("PATCH", `/shopping/${id}`, data),
  remove: (id) => request("DELETE", `/shopping/${id}`),
  clearChecked: () => request("POST", "/shopping/clear-checked"),
};

export const allergiesApi = {
  list: () => request("GET", "/allergies/"),
  save: (data) => request("POST", "/allergies/", data),
  update: (id, data) => request("PATCH", `/allergies/${id}`, data),
  remove: (id) => request("DELETE", `/allergies/${id}`),
};

export const peopleApi = {
  list: () => request("GET", "/people/"),
  listCompact: () => request("GET", "/people/?compact=1"),
  listDetailed: () => request("GET", "/people/?summary=1"),
  create: (data) => request("POST", "/people/", data),
  add: (data) => request("POST", "/people/", data),
  get: (id) => request("GET", `/people/${id}`),
  update: (id, data) => request("PATCH", `/people/${id}`, data),
  delete: (id) => request("DELETE", `/people/${id}`),
  remove: (id) => request("DELETE", `/people/${id}`),
  copyToWorkspace: (id, workspaceId) => request("POST", `/people/${id}/copy-to-workspace`, { workspace_id: workspaceId }),
  copyFromShare: (shareId) => request("POST", "/people/copy-from-share", { shareId }),
  getHistory: (id) => request("GET", `/people/${id}/history`),
  addHistory: (id, data) => request("POST", `/people/${id}/history`, data),
  deleteHistory: (id, historyId) => request("DELETE", `/people/${id}/history/${historyId}`),
};

export const occasionsApi = {
  list: () => request("GET", "/occasions/"),
  upcoming: (days = 90) => request("GET", `/occasions/upcoming?days=${days}`),
  create: (data) => request("POST", "/occasions/", data),
  add: (data) => request("POST", "/occasions/", data),
  update: (id, data) => request("PATCH", `/occasions/${id}`, data),
  delete: (id) => request("DELETE", `/occasions/${id}`),
  remove: (id) => request("DELETE", `/occasions/${id}`),
  copyToWorkspace: (id, workspaceId) => request("POST", `/occasions/${id}/copy-to-workspace`, { workspace_id: workspaceId }),
};

export const eventsApi = {
  generateShareLink: (eventId) => request("POST", `/events/${eventId}/share`, {}),
  generatePersonalShareLink: (eventId) => request("POST", `/events/personal/${eventId}/share`, {}),
  listRsvps:         (eventId) => request("GET",  `/events/${eventId}/rsvps`),
  listPersonalRsvps: (eventId) => request("GET",  `/events/personal/${eventId}/rsvps`),
  recentActivity:    ()        => request("GET",  "/events/activity"),
  rsvp:              (slug, data) => request("POST", `/events/public/${slug}/rsvp`, data),
  // Personal events (Events & Trips module storage)
  listPersonal:   ()              => request("GET",    "/events/personal/"),
  createPersonal: (data)          => request("POST",   "/events/personal/", data),
  updatePersonal: (id, data)      => request("PATCH",  `/events/personal/${id}`, data),
  removePersonal: (id)            => request("DELETE", `/events/personal/${id}`),
};

export const eventPlanApi = {
  get:      (entityType, entityId)        => request("GET",   `/event-plans/${entityType}/${entityId}`),
  update:   (entityType, entityId, data)  => request("PATCH", `/event-plans/${entityType}/${entityId}`, data),
  generate: (entityType, entityId, data)  => request("POST",  `/event-plans/generate/${entityType}/${entityId}`, data),
};

export const groupsApi = {
  list:        ()               => request("GET",    "/groups/"),
  create:      (data)           => request("POST",   "/groups/", data),
  get:         (id)             => request("GET",    `/groups/${id}`),
  update:      (id, data)       => request("PATCH",  `/groups/${id}`, data),
  remove:      (id)             => request("DELETE", `/groups/${id}`),
  allEvents:   ()               => request("GET",    "/groups/events/all"),
  createEvent: (groupId, data)  => request("POST",   `/groups/${groupId}/events`, data),
  getEvent:    (groupId, evtId) => request("GET",    `/groups/${groupId}/events/${evtId}`),
  updateEvent: (groupId, evtId, data) => request("PATCH", `/groups/${groupId}/events/${evtId}`, data),
  deleteEvent: (groupId, evtId) => request("DELETE", `/groups/${groupId}/events/${evtId}`),
};

export const giftsApi = {
  planner: () => request("GET", "/gifts/planner"),
  occasionContext: (occasionId) => request("GET", `/gifts/occasion/${occasionId}`),
  getHistory: (occasionId) => request("GET", `/gifts/history/${occasionId}`),
  suggest: (occasionId, options = {}) => request("POST", `/gifts/suggest/${occasionId}`, options),
  history: (occasionId) => request("GET", `/gifts/history/${occasionId}`),
  save: (id) => request("POST", `/gifts/${id}/save`, {}),
  unsave: (id) => request("POST", `/gifts/${id}/unsave`, {}),
  purchased: (id, data = {}) => request("POST", `/gifts/${id}/purchased`, data),
  markPurchased: (id, data = {}) => request("POST", `/gifts/${id}/purchased`, data),
  unmarkPurchased: (id) => request("POST", `/gifts/${id}/unpurchased`, {}),
  trackClick: (id) => request("POST", `/gifts/${id}/click`, {}),
  getCardMessage: (occasionId, options = {}) => request("POST", `/gifts/message/${occasionId}`, options),
  getSavedForPerson: (personId) => request("GET", `/gifts/saved/${personId}`),
};

export const workspacesApi = {
  list: () => request("GET", "/workspaces/"),
  current: () => request("GET", "/workspaces/current"),
  updateCurrent: (data) => request("PATCH", "/workspaces/current", data),
  switch: (workspaceId) => request("POST", "/workspaces/switch", { workspace_id: workspaceId }),
  inviteMember: (data) => request("POST", "/workspaces/members", data),
  updateMemberRole: (membershipId, role) => request("PATCH", `/workspaces/members/${membershipId}/role`, { role }),
  removeMember: (membershipId) => request("DELETE", `/workspaces/members/${membershipId}`),
  transferOwnership: (membershipId) => request("POST", "/workspaces/transfer-ownership", { membership_id: Number(membershipId) }),
  leave: () => request("POST", "/workspaces/leave", {}),
  repairDuplicatePeople: () => request("POST", "/workspaces/repair-duplicate-people", {}),
};

export const familyApi = {
  createInvite: (data) => request("POST", "/family/invites", data),
  listInvites: () => request("GET", "/family/invites"),
  revokeInvite: (inviteId) => request("DELETE", `/family/invites/${inviteId}`),
  resendInvite: (inviteId) => request("POST", `/family/invites/${inviteId}/resend`, {}),
  getInvite: (token) => request("GET", `/family/invites/${token}`),
  acceptInvite: (token) => request("POST", `/family/invites/${token}/accept`, {}),
  livelySummary: () => request("GET", "/family/lively-summary"),
  activityFeed: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request("GET", `/family/activity-feed${qs ? `?${qs}` : ""}`);
  },
  activityArchive: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request("GET", `/family/activity-feed/archive${qs ? `?${qs}` : ""}`);
  },
  cookingTonight: () => request("GET", "/family/cooking-tonight"),
  setCookingTonight: (mealName, mealType = "dinner") => request("POST", "/family/cooking-tonight", { meal_name: mealName, meal_type: mealType }),
  clearCookingTonight: () => request("DELETE", "/family/cooking-tonight"),
  reactToFeedItem: (itemId, emoji) => request("POST", `/family/feed/${itemId}/react`, { emoji }),
};

export const vaultApi = {
  listItems: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request("GET", `/vault/items${qs ? `?${qs}` : ""}`);
  },
  addItem: (data) => request("POST", "/vault/items", data),
  getItem: (id) => request("GET", `/vault/items/${id}`),
  updateItem: (id, data) => request("PUT", `/vault/items/${id}`, data),
  removeItem: (id) => request("DELETE", `/vault/items/${id}`),
  addServiceLog: (id, data) => request("POST", `/vault/items/${id}/service-log`, data),
  serviceLogs: (id) => request("GET", `/vault/items/${id}/service-logs`),
  documents: (id) => request("GET", `/vault/items/${id}/documents`),
  addDocument: (id, data) => request("POST", `/vault/items/${id}/documents`, data),
  recallCheck: (id) => request("POST", `/vault/items/${id}/recall-check`, {}),
  getLease: () => request("GET", "/vault/lease"),
  updateLease: (data) => request("PUT", "/vault/lease", data),
  healthScore: () => request("GET", "/vault/health-score"),
  readiness: () => request("GET", "/vault/readiness"),
  insights: () => request("GET", "/vault/insights"),
  budget: () => request("GET", "/vault/budget"),
  addBudgetItem: (data) => request("POST", "/vault/budget", data),
  updateBudgetItem: (id, data) => request("PUT", `/vault/budget/${id}`, data),
  removeBudgetItem: (id) => request("DELETE", `/vault/budget/${id}`),
  generateBudget: () => request("POST", "/vault/budget/generate", {}),
  aiBudget: (data = {}) => request("POST", "/vault/budget/ai-generate", data),
  saveAiBudget: (data = {}) => request("POST", "/vault/budget/ai-save", data),
  improvements: () => request("GET", "/vault/improvements"),
  addImprovement: (data) => request("POST", "/vault/improvements", data),
  updateImprovement: (id, data) => request("PUT", `/vault/improvements/${id}`, data),
  removeImprovement: (id) => request("DELETE", `/vault/improvements/${id}`),
  improvementRoi: (id, data = {}) => request("POST", `/vault/improvements/${id}/roi`, data),
  ownerSuggestions: () => request("GET", "/vault/owner-suggestions"),
  contractors: () => request("GET", "/vault/contractors"),
  addContractor: (data) => request("POST", "/vault/contractors", data),
  updateContractor: (id, data) => request("PUT", `/vault/contractors/${id}`, data),
  removeContractor: (id) => request("DELETE", `/vault/contractors/${id}`),
  seasonal: (season) => request("GET", `/vault/seasonal${season ? `?season=${encodeURIComponent(season)}` : ""}`),
  exportSummary: () => request("GET", "/vault/export/summary"),
  updatePropertyType: (property_type) => request("PATCH", "/vault/property-type", { property_type }),
  scoreItem: (id, data = {}) => request("POST", `/vault/items/${id}/score`, data),
  audit: (data = {}) => request("POST", "/vault/audit", data),
  askItem: (id, data = {}) => request("POST", `/vault/items/${id}/ask`, data),
  claim: (id, data = {}) => request("POST", `/vault/items/${id}/claim`, data),
  claimOther: (data = {}) => request("POST", "/vault/claim", data),
  negotiateLease: (data = {}) => request("POST", "/vault/lease/negotiate", data),
  presaleAudit: (data = {}) => request("POST", "/vault/presale-audit", data),
  quoteCheck: (data = {}) => request("POST", "/vault/quote-check", data),
  exportSalePdf: async () => {
    const token = getAuthToken();
    const headers = { "X-Requested-With": "XMLHttpRequest" };
    if (token) headers["Authorization"] = `Bearer ${token}`;
    const res = await fetch(`${BASE}/vault/export/pdf`, { headers });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "housemind-home-maintenance-resume.pdf";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  },
};

export const homeAiApi = {
  // Devices
  listDevices: () => request("GET", "/home-ai/devices"),
  getDevice: (id) => request("GET", `/home-ai/devices/${id}`),
  createDevice: (data) => request("POST", "/home-ai/devices", data),
  updateDevice: (id, data) => request("PATCH", `/home-ai/devices/${id}`, data),
  getDeviceSettings: (id) => request("GET", `/home-ai/devices/${id}/settings`),
  updateDeviceSettings: (id, data) => request("PATCH", `/home-ai/devices/${id}/settings`, data),
  getDeviceEquipment: (id) => request("GET", `/home-ai/devices/${id}/equipment`),
  confirmDeviceEquipment: (id, data) => request("PUT", `/home-ai/devices/${id}/equipment`, data),
  unlinkDeviceEquipment: (id) => request("DELETE", `/home-ai/devices/${id}/equipment`),
  completeDeviceMaintenance: (id, type, completedOn) => request("POST", `/home-ai/devices/${id}/maintenance/complete`, { type, completed_on: completedOn }),
  dismissDeviceMaintenance: (id, key) => request("POST", `/home-ai/devices/${id}/maintenance/dismiss`, { key }),
  deleteDevice: (id) => request("DELETE", `/home-ai/devices/${id}`),
  pollDevice: (id) => request("POST", `/home-ai/devices/${id}/poll`),
  recordDeviceSnapshot: (id, data) => request("POST", `/home-ai/devices/${id}/snapshot`, data),
  createDeviceBridgeToken: (id) => request("POST", `/home-ai/devices/${id}/bridge-token`),
  getReadings: (id, params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request("GET", `/home-ai/devices/${id}/readings${qs ? `?${qs}` : ""}`);
  },
  // Alerts
  listAlerts: (includeDismissed = false) =>
    request("GET", `/home-ai/alerts${includeDismissed ? "?dismissed=true" : ""}`),
  markAlertRead: (id) => request("PATCH", `/home-ai/alerts/${id}/read`),
  dismissAlert: (id) => request("PATCH", `/home-ai/alerts/${id}/dismiss`),
  markAllRead: () => request("POST", "/home-ai/alerts/mark-all-read"),
  deleteAlerts: (ids) => request("POST", "/home-ai/alerts/delete", { ids }),
  deleteAllAlerts: () => request("POST", "/home-ai/alerts/delete", { all: true }),
  // Integrations
  listIntegrations: () => request("GET", "/home-ai/integrations"),
  getNestAuthUrl: (redirectUri) =>
    request("GET", `/home-ai/integrations/nest/auth-url?redirect_uri=${encodeURIComponent(redirectUri)}`),
  nestCallback: (code, redirectUri) =>
    request("POST", "/home-ai/integrations/nest/callback", { code, redirect_uri: redirectUri }),
  getSmartThingsAuthUrl: (redirectUri) =>
    request("GET", `/home-ai/integrations/smartthings/auth-url?redirect_uri=${encodeURIComponent(redirectUri)}`),
  smartThingsCallback: (code, state, redirectUri) =>
    request("POST", "/home-ai/integrations/smartthings/callback", { code, state, redirect_uri: redirectUri }),
  connectHomeAssistant: (payload) => request("POST", "/home-ai/integrations/home-assistant/connect", payload),
  disconnectIntegration: (brand) => request("DELETE", `/home-ai/integrations/${brand}`),
  currentWeather: () => request("GET", "/home-ai/weather/current"),
  // Analytics
  runtimeSummary: (deviceId, days = 7) =>
    request("GET", `/home-ai/analytics/runtime-summary?device_id=${deviceId}&days=${days}`),
  temperatureHistory: (deviceId, hours = 24) =>
    request("GET", `/home-ai/analytics/temperature-history?device_id=${deviceId}&hours=${hours}`),
  energySummary: () => request("GET", "/home-ai/analytics/energy-summary"),
  applianceHealth: () => request("GET", "/home-ai/analytics/appliance-health"),
  // AI Chat
  analysis: () => request("GET", "/home-ai/ai/analysis"),
  ask: (question, history = []) => request("POST", "/home-ai/ai/ask", { question, history }),
};

export const homeCareApi = {
  snapshot: () => request("GET", "/home-care"),
  refresh: () => request("POST", "/home-care/refresh", {}),
  createTask: (data) => request("POST", "/home-care/tasks", data),
  updateTask: (id, data) => request("PATCH", `/home-care/tasks/${id}`, data),
  snoozeTask: (id, data = {}) => request("POST", `/home-care/tasks/${id}/snooze`, data),
  completeTask: (id, data = {}) => request("POST", `/home-care/tasks/${id}/complete`, data),
  dismissTask: (id) => request("POST", `/home-care/tasks/${id}/dismiss`, {}),
};

export const propertyProfileApi = {
  snapshot: () => request("GET", "/property-profile"),
  update: (data) => request("PATCH", "/property-profile", data),
  createSpace: (data) => request("POST", "/property-profile/spaces", data),
  updateSpace: (id, data) => request("PATCH", `/property-profile/spaces/${id}`, data),
  deleteSpace: (id) => request("DELETE", `/property-profile/spaces/${id}`),
  linkVault: (id, propertySpaceId) => request("PATCH", `/property-profile/links/vault/${id}`, { property_space_id: propertySpaceId || null }),
  linkDevice: (id, propertySpaceId) => request("PATCH", `/property-profile/links/device/${id}`, { property_space_id: propertySpaceId || null }),
  createInsurance: (data) => request("POST", "/property-profile/insurance", data),
  scanInsurance: async (file) => {
    const form = new FormData();
    form.append("file", await prepareScanUpload(file));
    const token = getAuthToken();
    const headers = { "X-Requested-With": "XMLHttpRequest" };
    if (token) headers.Authorization = `Bearer ${token}`;
    const res = await fetch(`${BASE}/property-profile/insurance/scan`, {
      method: "POST",
      headers,
      body: form,
    });
    if (res.status === 401) {
      clearAuthTokens();
      window.dispatchEvent(new Event("hm_signout"));
      throw new Error("Session expired. Please sign in again.");
    }
    if (!res.ok) {
      let message = scanUploadErrorMessage(res);
      try {
        const payload = await res.json();
        message = payload.message || payload.error || message;
      } catch {}
      throw new Error(message);
    }
    return res.json();
  },
  updateInsurance: (id, data) => request("PATCH", `/property-profile/insurance/${id}`, data),
  deleteInsurance: (id) => request("DELETE", `/property-profile/insurance/${id}`),
  createClaim: (data) => request("POST", "/property-profile/claims", data),
  updateClaim: (id, data) => request("PATCH", `/property-profile/claims/${id}`, data),
  deleteClaim: (id) => request("DELETE", `/property-profile/claims/${id}`),
};

export const emergencySafetyApi = {
  snapshot: () => request("GET", "/emergency-safety"),
  updatePlan: (data) => request("PATCH", "/emergency-safety", data),
  deletePlan: () => request("DELETE", "/emergency-safety"),
  createResource: (data) => request("POST", "/emergency-safety/resources", data),
  scanResource: async (file) => {
    const form = new FormData();
    form.append("file", await prepareScanUpload(file));
    const token = getAuthToken();
    const headers = { "X-Requested-With": "XMLHttpRequest" };
    if (token) headers.Authorization = `Bearer ${token}`;
    const res = await fetch(`${BASE}/emergency-safety/resources/scan`, { method: "POST", headers, body: form });
    if (!res.ok) {
      let message = scanUploadErrorMessage(res);
      try { const payload = await res.json(); message = payload.message || payload.error || message; } catch {}
      throw new Error(message);
    }
    return res.json();
  },
  updateResource: (id, data) => request("PATCH", `/emergency-safety/resources/${id}`, data),
  deleteResource: (id) => request("DELETE", `/emergency-safety/resources/${id}`),
  verifyResource: (id) => request("POST", `/emergency-safety/resources/${id}/verify`, {}),
  createContact: (data) => request("POST", "/emergency-safety/contacts", data),
  updateContact: (id, data) => request("PATCH", `/emergency-safety/contacts/${id}`, data),
  deleteContact: (id) => request("DELETE", `/emergency-safety/contacts/${id}`),
  createDrill: (data) => request("POST", "/emergency-safety/drills", data),
  updateDrill: (id, data) => request("PATCH", `/emergency-safety/drills/${id}`, data),
  deleteDrill: (id) => request("DELETE", `/emergency-safety/drills/${id}`),
  completeDrill: (id, data = {}) => request("POST", `/emergency-safety/drills/${id}/complete`, data),
};

export const petsApi = {
  snapshot: () => request("GET", "/pets"),
  create: (data) => request("POST", "/pets", data),
  update: (id, data) => request("PATCH", `/pets/${id}`, data),
  remove: (id) => request("DELETE", `/pets/${id}`),
  restore: (id) => request("POST", `/pets/${id}/restore`, {}),
  removePermanent: (id) => request("DELETE", `/pets/${id}/permanent`),
  createSchedule: (petId, data) => request("POST", `/pets/${petId}/schedules`, data),
  updateSchedule: (id, data) => request("PATCH", `/pets/schedules/${id}`, data),
  confirmSchedule: (id) => request("POST", `/pets/schedules/${id}/confirm`, {}),
  removeSchedule: (id) => request("DELETE", `/pets/schedules/${id}`),
    completeSchedule: (id, data = {}) => request("POST", `/pets/schedules/${id}/complete`, data),
    updateFollowUp: (id, data) => request("PATCH", `/pets/care-logs/${id}/follow-up`, data),
    linkVault: (petId, itemId) => request("PATCH", `/pets/${petId}/vault/${itemId}`, {}),
    createRoutine: (petId, data) => request("POST", `/pets/${petId}/routines`, data),
    updateRoutine: (id, data) => request("PATCH", `/pets/routines/${id}`, data),
    removeRoutine: (id) => request("DELETE", `/pets/routines/${id}`),
    completeRoutine: (id, completed = true) => request("POST", `/pets/routines/${id}/today`, { completed }),
    createFood: (petId, data) => request("POST", `/pets/${petId}/food`, data),
    updateFood: (id, data) => request("PATCH", `/pets/food/${id}`, data),
    removeFood: (id) => request("DELETE", `/pets/food/${id}`),
    addFoodToShopping: (id, data = {}) => request("POST", `/pets/food/${id}/shopping`, data),
    restockFood: (id, data) => request("POST", `/pets/food/${id}/restock`, data),
    createHealthEntry: (petId, data) => request("POST", `/pets/${petId}/health`, data),
    importHistory: (petId, data) => request("POST", `/pets/${petId}/history`, data),
    removeHealthEntry: (id) => request("DELETE", `/pets/health/${id}`),
    createInsuranceClaim: (petId, data) => request("POST", `/pets/${petId}/insurance-claims`, data),
    updateInsuranceClaim: (id, data) => request("PATCH", `/pets/insurance-claims/${id}`, data),
    removeInsuranceClaim: (id) => request("DELETE", `/pets/insurance-claims/${id}`),
    caregiverBrief: (petId) => request("GET", `/pets/${petId}/caregiver-brief`),
    vetVisitBrief: (petId) => request("GET", `/pets/${petId}/vet-visit-brief`),
    createLostShare: (petId, data) => request("POST", `/pets/${petId}/lost-share`, data),
    revokeLostShare: (id) => request("DELETE", `/pets/lost-share/${id}`),
    downloadReport: async (petId, kind, details = {}) => {
      const token = getAuthToken();
      const query = new URLSearchParams(Object.entries(details).filter(([, value]) => value != null && value !== "")).toString();
      const response = await fetch(`${BASE}/pets/${petId}/report/${kind}.pdf${query ? `?${query}` : ""}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      if (!response.ok) { const payload = await response.json().catch(() => ({})); throw new Error(payload.error || "PDF could not be prepared."); }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob); const link = document.createElement("a");
      link.href = url; link.download = `${kind}-pet-report.pdf`; link.click(); URL.revokeObjectURL(url);
    },
    scanRecord: async (petId, file, consent) => {
      const token = getAuthToken();
      const headers = { "X-Requested-With": "XMLHttpRequest" };
      if (token) headers.Authorization = `Bearer ${token}`;
      const form = new FormData();
      form.append("file", file);
      form.append("consent", consent ? "true" : "false");
      const res = await fetch(`${BASE}/pets/${petId}/records/scan`, { method: "POST", headers, body: form });
      const payload = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(payload.message || payload.error || `Upload failed (${res.status})`);
      return payload;
    },
  };

export const moneyApi = {
  overview: (month) => request("GET", `/money${month ? `?month=${encodeURIComponent(month)}` : ""}`),
  updateBudget: (data) => request("PATCH", "/money/budget", data),
};

export const contractsApi = {
  list: (status) => request("GET", `/contracts${status ? `?status=${status}` : ""}`),
  get: (id) => request("GET", `/contracts/${id}`),
  scan: async (file) => {
    const token = getAuthToken();
    const headers = { "X-Requested-With": "XMLHttpRequest" };
    if (token) headers["Authorization"] = `Bearer ${token}`;
    const form = new FormData();
    form.append("file", file);
    const res = await fetch(`${BASE}/contracts/scan`, { method: "POST", headers, body: form });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || `Upload failed (${res.status})`);
    }
    return res.json();
  },
  scanText: (text) => request("POST", "/contracts/scan", { text }),
  update: (id, data) => request("PATCH", `/contracts/${id}`, data),
  remove: (id) => request("DELETE", `/contracts/${id}`),
};

export const sharingApi = {
  summary: () => request("GET", "/sharing/summary"),
  members: () => request("GET", "/sharing/members"),
  create: (data) => request("POST", "/sharing/", data),
  detail: (id) => request("GET", `/sharing/${id}/detail`),
  updatePermission: (id, permission) => request("PATCH", `/sharing/${id}/permission`, { permission }),
  updateScope: (id, scope) => request("PATCH", `/sharing/${id}/scope`, { scope }),
  completePetRoutine: (shareId, routineId, completed = true) => request("POST", `/sharing/${shareId}/pet-routines/${routineId}/today`, { completed }),
  revoke: (id) => request("DELETE", `/sharing/${id}`),
  hide: (id) => request("POST", `/sharing/${id}/hide`, {}),
};

export const sharedApi = {
  get: (id) => request("GET", `/shared/${id}`),
  saveRecipe: (id) => request("POST", `/shared/${id}/save-recipe`, {}),
  addMealPlan: (id, data = {}) => request("POST", `/shared/${id}/add-meal-plan`, data),
  addPantry: (id) => request("POST", `/shared/${id}/add-pantry`, {}),
};

export const billingApi = {
  plans: () => request("GET", "/billing/plans"),
  status: () => request("GET", "/billing/status"),
  createCheckoutSession: (plan) => request("POST", "/billing/checkout-session", { plan }),
  createPortalSession: () => request("POST", "/billing/portal-session", {}),
  cancelSubscription: () => request("POST", "/billing/cancel-subscription", {}),
};

export const supportApi = {
  contact: (data) => request("POST", "/support/contact", data),
};

export const adminApi = {
  overview:              () => request("GET",    "/admin/overview"),
  health:                () => request("GET",    "/admin/health"),
  resetUserAiUsage:      (userId) => request("POST",   `/admin/users/${userId}/reset-ai-usage`, {}),
  extendTrial:           (userId, data) => request("POST",   `/admin/users/${userId}/extend-trial`, data),
  cancelSubscription:    (userId, data) => request("POST",   `/admin/users/${userId}/cancel-subscription`, data),
  pauseSubscription:     (userId, data) => request("POST",   `/admin/users/${userId}/pause-subscription`, data),
  emailUser:             (userId, data) => request("POST",   `/admin/users/${userId}/email`, data),
  updateUserAccess:      (userId, data) => request("PATCH",  `/admin/users/${userId}/access`, data),
  removeUserAccess:      (userId) => request("DELETE", `/admin/users/${userId}/access`),
  deleteUser:            (userId) => request("DELETE", `/admin/users/${userId}`),
  updateUserStripe:      (userId, data) => request("PATCH",  `/admin/users/${userId}/stripe`, data),
  updateWorkspaceAccess: (workspaceId, data) => request("PATCH",  `/admin/workspaces/${workspaceId}/access`, data),
  removeWorkspaceAccess: (workspaceId) => request("DELETE", `/admin/workspaces/${workspaceId}/access`),
  broadcastEmail:        (data) => request("POST",   "/admin/broadcast", data),
  getSystemNotice:       () => request("GET",    "/admin/system-notice"),
  setSystemNotice:       (data) => request("POST",   "/admin/system-notice", data),
  clearSystemNotice:     () => request("DELETE", "/admin/system-notice"),
  runReminders:          () => request("POST",   "/admin/reminders/run", {}),
  runDiscoverySync:      (data = {}) => request("POST",   "/admin/discovery-sync/run", data),
  discoverySyncHealth:   (limit = 100) => request("GET",    `/admin/discovery-sync/health?limit=${limit}`),
  runIntelligenceAll:    () => request("POST",   "/admin/intelligence/run-all", {}),
  runIntelligenceUser:   (userId) => request("POST",   `/admin/intelligence/run-user/${userId}`, {}),
  insights:              (limit = 75) => request("GET",    `/admin/intelligence/insights?limit=${limit}`),
  supportRequests:       (status) => request("GET",    `/admin/support-requests${status ? `?status=${encodeURIComponent(status)}` : ""}`),
  resolveSupportRequest: (requestId, data = {}) => request("POST",   `/admin/support-requests/${requestId}/resolve`, data),
  exportUsers:           () => request("GET",    "/admin/export/users"),
  exportBilling:         () => request("GET",    "/admin/export/billing"),
  auditLogs:             (limit = 100) => request("GET",    `/admin/audit-logs?limit=${limit}`),
  securityEvents:         (limit = 100) => request("GET",    `/admin/security-events?limit=${limit}`),
  // ── User flags & CRM notes ──────────────────────────────────────────────
  flagUser:              (userId, data) => request("POST",   `/admin/users/${userId}/flag`, data),
  addUserNote:           (userId, data) => request("POST",   `/admin/users/${userId}/notes`, data),
  getUserNotes:          (userId) => request("GET",    `/admin/users/${userId}/notes`),
  // ── Impersonation ───────────────────────────────────────────────────────
  impersonateUser:       (userId) => request("POST",   `/admin/users/${userId}/impersonate`, {}),
  // ── Refunds & billing actions ───────────────────────────────────────────
  recordRefund:          (userId, data) => request("POST",   `/admin/users/${userId}/refund`, data),
  // ── Feature flags ───────────────────────────────────────────────────────
  getFeatureFlags:       () => request("GET",    "/admin/feature-flags"),
  setFeatureFlag:        (data) => request("POST",   "/admin/feature-flags", data),
  // ── GDPR / Compliance ───────────────────────────────────────────────────
  gdprQueue:             () => request("GET",    "/admin/compliance/queue"),
  gdprProcess:           (requestId, data) => request("POST",   `/admin/compliance/${requestId}/process`, data),
  gdprExportUser:        (userId) => request("POST",   `/admin/users/${userId}/gdpr-export`, {}),
};

export const affiliateApi = {
  track:            (program, module, url) => request("POST", "/affiliate/track", { program, module, url }),
  tripLinks:        (params)  => request("GET", `/affiliate/links/trips?${new URLSearchParams(params)}`),
  giftLinks:        (params)  => request("GET", `/affiliate/links/gift?${new URLSearchParams(params)}`),
  packingItemLink:  (item, trip_style) => request("GET", `/affiliate/links/packing-item?item=${encodeURIComponent(item)}&trip_style=${encodeURIComponent(trip_style || "")}`),
  vehicleLinks:     (params)  => request("GET", `/affiliate/links/vehicle-service?${new URLSearchParams(params)}`),
  foodLinks:        ()        => request("GET", "/affiliate/links/food"),
  vaultLinks:       (query)   => request("GET", `/affiliate/links/home-vault?query=${encodeURIComponent(query || "")}`),
};

export const vehiclesApi = {
  list:              ()              => request("GET",    "/vehicles/"),
  create:            (data)          => request("POST",   "/vehicles/", data),
  update:            (id, data)      => request("PATCH",  `/vehicles/${id}`, data),
  remove:            (id)            => request("DELETE", `/vehicles/${id}`),
  decodeVin:         (vin)           => request("GET",    `/vehicles/vin/${vin}`),
  fuelPrices:        (state)         => request("GET",    `/vehicles/fuel-prices${state ? `?state=${encodeURIComponent(state)}` : ""}`),
  decodeObdCodes:    (codes)         => request("POST",   "/vehicles/obd-codes", { codes }),
  listService:       (id)            => request("GET",    `/vehicles/${id}/service`),
  listServiceGrouped:(id)            => request("GET",    `/vehicles/${id}/service?grouped=1`),
  addService:        (id, data)      => request("POST",   `/vehicles/${id}/service`, data),
  updateService:     (id, sid, data) => request("PATCH",  `/vehicles/${id}/service/${sid}`, data),
  deleteService:     (id, sid)       => request("DELETE", `/vehicles/${id}/service/${sid}`),
  listFuel:          (id)            => request("GET",    `/vehicles/${id}/fuel`),
  addFuel:           (id, data)      => request("POST",   `/vehicles/${id}/fuel`, data),
  updateFuel:        (id, fid, data) => request("PATCH",  `/vehicles/${id}/fuel/${fid}`, data),
  deleteFuel:        (id, fid)       => request("DELETE", `/vehicles/${id}/fuel/${fid}`),
  getInsurance:      (id)            => request("GET",    `/vehicles/${id}/insurance`),
  saveInsurance:     (id, data)      => request("PUT",    `/vehicles/${id}/insurance`, data),
  getRegistration:   (id)            => request("GET",    `/vehicles/${id}/registration`),
  saveRegistration:  (id, data)      => request("PUT",    `/vehicles/${id}/registration`, data),
  vehicleVault:      (id)            => request("GET",    `/vehicles/${id}/vault`),
  listDiagnostics:   (id)            => request("GET",    `/vehicles/${id}/diagnostics`),
  addDiagnostic:     (id, data)      => request("POST",   `/vehicles/${id}/diagnostics`, data),
  updateDiagnostic:  (id, sid, data) => request("PATCH",  `/vehicles/${id}/diagnostics/${sid}`, data),
  deleteDiagnostic:  (id, sid)       => request("DELETE", `/vehicles/${id}/diagnostics/${sid}`),
  maintenanceSchedule:(id)           => request("GET",    `/vehicles/${id}/maintenance-schedule`),
  addMilestone:      (id, data)      => request("POST",   `/vehicles/${id}/milestones`, data),
  updateMilestone:   (id, mid, data) => request("PATCH",  `/vehicles/${id}/milestones/${mid}`, data),
  deleteMilestone:   (id, mid)       => request("DELETE", `/vehicles/${id}/milestones/${mid}`),
  listClaims:        (id)            => request("GET",    `/vehicles/${id}/claims`),
  addClaim:          (id, data)      => request("POST",   `/vehicles/${id}/claims`, data),
  updateClaim:       (id, cid, data) => request("PATCH",  `/vehicles/${id}/claims/${cid}`, data),
  deleteClaim:       (id, cid)       => request("DELETE", `/vehicles/${id}/claims/${cid}`),
  intelligence:      (id)            => request("GET",    `/vehicles/${id}/intelligence`),
  markRecallsReviewed:(id)           => request("POST",   `/vehicles/${id}/recalls/reviewed`, {}),
  maintenanceAdvice: (id)            => request("POST",   `/vehicles/${id}/maintenance-advice`, {}),
  quoteCheck:        (id, data)      => request("POST",   `/vehicles/${id}/quote-check`, data),
  exportPdf: async (vehicleId) => {
    const token = getAuthToken();
    const headers = { "X-Requested-With": "XMLHttpRequest" };
    if (token) headers["Authorization"] = `Bearer ${token}`;
    const res = await fetch(`${BASE}/vehicles/${vehicleId}/export.pdf`, { headers });
    if (res.status === 401) {
      clearAuthTokens();
      window.dispatchEvent(new Event("hm_signout"));
      throw new Error("Session expired. Please sign in again.");
    }
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    const disposition = res.headers.get("Content-Disposition") || "";
    const match = disposition.match(/filename="?([^"]+)"?/i);
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = match?.[1] || "housemind-vehicle-record.pdf";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  },
  emailExport:        (id)            => request("POST",   `/vehicles/${id}/export/email`, {}),
  scanReceipt: async (vehicleId, file) => {
    const form = new FormData();
    form.append("file", await prepareScanUpload(file));
    const token = getAuthToken();
    const headers = { "X-Requested-With": "XMLHttpRequest" };
    if (token) headers["Authorization"] = `Bearer ${token}`;
    return fetch(`${BASE}/vehicles/${vehicleId}/service/scan`, { method: "POST", headers, body: form })
      .then(async res => {
        if (res.status === 401) { clearAuthTokens(); window.dispatchEvent(new Event("hm_signout")); throw new Error("Session expired. Please sign in again."); }
        if (!res.ok) { let msg = scanUploadErrorMessage(res); try { msg = (await res.json()).error || msg; } catch {} throw new Error(msg); }
        return res.json();
      });
  },
  scanQuote: async (vehicleId, file) => {
    const form = new FormData();
    form.append("file", await prepareScanUpload(file));
    const token = getAuthToken();
    const headers = { "X-Requested-With": "XMLHttpRequest" };
    if (token) headers["Authorization"] = `Bearer ${token}`;
    return fetch(`${BASE}/vehicles/${vehicleId}/quote/scan`, { method: "POST", headers, body: form })
      .then(async res => {
        if (res.status === 401) { clearAuthTokens(); window.dispatchEvent(new Event("hm_signout")); throw new Error("Session expired. Please sign in again."); }
        if (!res.ok) { let msg = scanUploadErrorMessage(res); try { msg = (await res.json()).error || msg; } catch {} throw new Error(msg); }
        return res.json();
      });
  },
};

export const subscriptionsApi = {
  list:           (status)   => request("GET",    status ? `/subscriptions/?status=${status}` : "/subscriptions/"),
  create:         (data)     => request("POST",   "/subscriptions/", data),
  update:         (id, data) => request("PATCH",  `/subscriptions/${id}`, data),
  remove:         (id)       => request("DELETE", `/subscriptions/${id}`),
  logUsage:       (id, data) => request("POST",   `/subscriptions/${id}/usage`, data),
  getUsage:       (id)       => request("GET",    `/subscriptions/${id}/usage`),
  getPriceHistory:(id)       => request("GET",    `/subscriptions/${id}/price-history`),
  getAlerts:      ()         => request("GET",    "/subscriptions/alerts"),
  runDailyMaintenance: ()    => request("POST",   "/subscriptions/run-daily-maintenance", {}),
  signals:        ()         => request("GET",    "/subscriptions/signals"),
  dismissAlert:   (id, data) => request("POST",   `/subscriptions/alerts/${id}/dismiss`, data),
  savedWasteReport: ()       => request("GET",    "/subscriptions/waste-report"),
  wasteReport:    (data={})  => request("POST",   "/subscriptions/waste-report", data),
  keepScore:      (id)       => request("POST",   `/subscriptions/${id}/keep-score`, {}),
  audit:          (id)       => request("POST",   `/subscriptions/${id}/audit`, {}),
  getReport:      (id, type) => request("GET",    `/subscriptions/${id}/report/${type}`),
  generateReport: (id, type, data={}) => request("POST", `/subscriptions/${id}/report/${type}`, data),
  cancelScript:   (id, data) => request("POST",   `/subscriptions/${id}/cancel-script`, data),
  ask:            (data)     => request("POST",   "/subscriptions/ask", data),
};

export const utilitiesApi = {
  list:   ()         => request("GET",    "/utilities/"),
  create: (data)     => request("POST",   "/utilities/", data),
  update: (id, data) => request("PATCH",  `/utilities/${id}`, data),
  remove: (id)       => request("DELETE", `/utilities/${id}`),
  entries:(id)       => request("GET",    `/utilities/${id}/entries`),
  addEntry:(id, data)=> request("POST",   `/utilities/${id}/entries`, data),
  updateEntry:(billId, entryId, data) => request("PATCH", `/utilities/${billId}/entries/${entryId}`, data),
  markEntryPaid:(billId, entryId) => request("POST", `/utilities/${billId}/entries/${entryId}/mark-paid`, {}),
  deleteEntry:(billId, entryId) => request("DELETE", `/utilities/${billId}/entries/${entryId}`),
  ask:    (data)     => request("POST",   "/utilities/ask", data),
  intelligence: ()    => request("GET",    "/utilities/intelligence"),
  trends: ()          => request("GET",    "/utilities/trends"),
  getCrossModuleInsights: () => request("GET", "/utilities/insights"),
  dismissAlert: (id)  => request("POST",   `/utilities/alerts/${id}/dismiss`, {}),
  optimize: (id, data = {}) => request("POST",   `/utilities/${id}/optimize`, data),
  providerComparison: (id, data = {}) => request("POST", `/utilities/${id}/provider-comparison`, data),
  negotiation: (id, data = {}) => request("POST", `/utilities/${id}/negotiation`, data),
  serviceReview: (id, data = {}) => request("POST", `/utilities/${id}/service-review`, data),
  getReport: (id, type) => request("GET", `/utilities/${id}/report/${type}`),
  savedEfficiencyReport: () => request("GET", "/utilities/efficiency-report"),
  efficiencyReport: (data = {}) => request("POST",  "/utilities/efficiency-report", data),
  toggleAutoLog: (billId, enabled, autoLogDay) =>
    request("PATCH", `/utilities/${billId}/auto-log`, { enabled, auto_log_day: autoLogDay }),
  runAutoLog: () => request("POST", "/utilities/run-auto-log", {}),
  scanBill: async (file, billId) => {
    const form = new FormData();
    form.append("file", await prepareScanUpload(file));
    if (billId) form.append("bill_id", billId);
    const token = getAuthToken();
    const headers = { "X-Requested-With": "XMLHttpRequest" };
    if (token) headers["Authorization"] = `Bearer ${token}`;
    return fetch(`${BASE}/utilities/scan`, { method: "POST", headers, body: form })
      .then(async res => {
        if (res.status === 401) {
          clearAuthTokens();
          window.dispatchEvent(new Event("hm_signout"));
          throw new Error("Session expired. Please sign in again.");
        }
        if (!res.ok) {
          let msg = scanUploadErrorMessage(res);
          try {
            const payload = await res.json();
            msg = payload.message || payload.error || msg;
          } catch {}
          throw new Error(msg);
        }
        return res.json();
      });
  },
};

let dashboardBootstrapPromise = null;
let dashboardBootstrapCache = null;
let dashboardBootstrapAt = 0;
let dashboardWeatherPromise = null;
let dashboardWeatherCache = null;
let dashboardWeatherAt = 0;
let householdHealthPromise = null;
let householdHealthCache = null;
let householdHealthAt = 0;
let dashboardWidgetActive = 0;
const dashboardWidgetQueue = [];
const DASHBOARD_WIDGET_CONCURRENCY = 2;

const drainDashboardWidgetQueue = () => {
  if (dashboardWidgetActive >= DASHBOARD_WIDGET_CONCURRENCY) return;
  const job = dashboardWidgetQueue.shift();
  if (!job) return;
  dashboardWidgetActive += 1;
  job.load()
    .then(job.resolve, job.reject)
    .finally(() => {
      dashboardWidgetActive -= 1;
      drainDashboardWidgetQueue();
    });
  drainDashboardWidgetQueue();
};

const scheduleDashboardWidget = (load) => new Promise((resolve, reject) => {
  dashboardWidgetQueue.push({ load, resolve, reject });
  drainDashboardWidgetQueue();
});

const loadDashboardWidget = (path) => scheduleDashboardWidget(() => request("GET", path));

const loadDashboardBootstrap = (force = false) => {
  const fresh = dashboardBootstrapCache && Date.now() - dashboardBootstrapAt < 30_000;
  if (!force && fresh) return Promise.resolve(dashboardBootstrapCache);
  if (dashboardBootstrapPromise) return dashboardBootstrapPromise;
  dashboardBootstrapPromise = request("GET", "/dashboard/bootstrap")
    .then((payload) => {
      dashboardBootstrapCache = payload;
      dashboardBootstrapAt = Date.now();
      return payload;
    })
    .finally(() => { dashboardBootstrapPromise = null; });
  return dashboardBootstrapPromise;
};

const loadDashboardWeather = () => {
  if (dashboardWeatherCache && Date.now() - dashboardWeatherAt < 10 * 60_000) {
    return Promise.resolve(dashboardWeatherCache);
  }
  if (dashboardWeatherPromise) return dashboardWeatherPromise;
  dashboardWeatherPromise = request("GET", "/dashboard/weather/current")
    .then((payload) => {
      dashboardWeatherCache = payload;
      dashboardWeatherAt = Date.now();
      return payload;
    })
    .finally(() => { dashboardWeatherPromise = null; });
  return dashboardWeatherPromise;
};

const loadHouseholdHealth = (force = false) => {
  if (!force && householdHealthCache && Date.now() - householdHealthAt < 60_000) {
    return Promise.resolve(householdHealthCache);
  }
  if (householdHealthPromise) return householdHealthPromise;
  const operation = force
    ? request("POST", "/dashboard/household-health/refresh", {})
    : request("GET", "/dashboard/household-health?cached=1");
  householdHealthPromise = operation
    .then((payload) => {
      householdHealthCache = payload;
      householdHealthAt = Date.now();
      return payload;
    })
    .finally(() => { householdHealthPromise = null; });
  return householdHealthPromise;
};

export const dashboardApi = {
  scheduleWidget:    (load)   => scheduleDashboardWidget(load),
  bootstrap:         (force = false) => loadDashboardBootstrap(force),
  clearBootstrap:    () => { dashboardBootstrapCache = null; dashboardBootstrapAt = 0; },
  attentionItems:    ()       => request("GET",   "/dashboard/attention-items"),
  dismissAttention:  (item, action = "done") => request("POST",  "/dashboard/attention-items/dismiss", { ...item, action }),
  dismissAttentionBulk: (items, action = "done") => request("POST", "/dashboard/attention-items/dismiss-bulk", { items, action }),
  mealShortageState: (mealDayId) => request("GET", `/dashboard/attention-items/meal-shortage/${mealDayId}`),
  addMealShortageToShopping: (mealDayId, ingredients) => request("POST", "/dashboard/attention-items/meal-shortage/shopping", { meal_day_id: mealDayId, ingredients }),
  todayTasks:        (date)   => request("GET",   `/dashboard/today-tasks${date ? `?date=${encodeURIComponent(date)}` : ""}`),
  createTodayTask:   (data)   => request("POST",  "/dashboard/today-tasks", data),
  updateTodayTask:   (id, data) => request("PATCH", `/dashboard/today-tasks/${id}`, data),
  deleteTodayTask:   (id)     => request("DELETE", `/dashboard/today-tasks/${id}`),
  briefing:          (force)  => request("GET",   `/dashboard/briefing${force ? "?force=1" : ""}`),
  currentWeather:    ()       => loadDashboardWeather(),
  getConfig:         ()       => request("GET",   "/dashboard/config"),
  updateConfig:      (data)   => request("PATCH", "/dashboard/config", data),
  householdHealth:   ()       => loadHouseholdHealth(),
  refreshHouseholdHealth: ()  => loadHouseholdHealth(true),
  householdHealthTrend: (limit = 30) => request("GET", `/dashboard/household-health/trend?limit=${limit}`),
  widgetFood:        ()       => loadDashboardWidget("/dashboard/widgets/food-snapshot"),
  widgetOccasions:   ()       => loadDashboardWidget("/dashboard/widgets/occasions"),
  widgetVault:       ()       => loadDashboardWidget("/dashboard/widgets/vault-health"),
  widgetVehicles:    ()       => loadDashboardWidget("/dashboard/widgets/vehicle-status"),
  widgetSubs:        ()       => loadDashboardWidget("/dashboard/widgets/sub-spend"),
  widgetUtilities:   ()       => loadDashboardWidget("/dashboard/widgets/utilities"),
  widgetTimeline:    ()       => loadDashboardWidget("/dashboard/widgets/timeline"),
  // Intelligence insights
  insights:          ()           => request("GET",  "/dashboard/insights"),
  dismissInsight:    (id, acted)  => request("POST", `/dashboard/insights/${id}/dismiss`, { was_acted_on: !!acted }),
  predictions:       ()           => request("GET",  "/dashboard/predictions"),
  predictionAccuracy: ()          => request("GET",  "/dashboard/predictions/accuracy"),
  refreshPredictions: ()          => request("POST", "/dashboard/predictions/refresh", {}),
  dismissPrediction: (id)         => request("POST", `/dashboard/predictions/${id}/dismiss`, {}),
  runEngine:         ()           => request("POST", "/dashboard/run-engine"),
  ask:               (data)       => request("POST", "/dashboard/ask", data),
};

export const timelineApi = {
  list:    (params = {}) => request("GET", `/timeline${queryString(params)}`),
  summary: (params = {}) => request("GET", `/timeline/summary${queryString(params)}`),
};

export const importCandidatesApi = {
  list:      (typeOrParams) => {
    const params = typeof typeOrParams === "object" && typeOrParams !== null ? typeOrParams : { type: typeOrParams };
    return request("GET", `/import-candidates${queryString(params)}`);
  },
  count:     ()     => request("GET", "/import-candidates/count"),
  history:   (filters = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters || {}).forEach(([key, value]) => {
      if (value) params.set(key, value);
    });
    const query = params.toString();
    return request("GET", `/import-candidates/history${query ? `?${query}` : ""}`);
  },
  historyMeta: ()   => request("GET", "/import-candidates/history/meta"),
  deleteHistoryItem: (id) => request("DELETE", `/import-candidates/${id}`),
  sources:   ()     => request("GET", "/import-candidates/sources"),
  connections: ()    => request("GET", "/import-candidates/connections"),
  requestConnection: (id, data = {}) => request("POST", `/import-candidates/connections/${id}/request`, data),
  configureImapConnection: (id, data = {}) => request("POST", `/import-candidates/connections/${id}/imap`, data),
  startConnectionOauth: (id) => request("POST", `/import-candidates/connections/${id}/oauth/start`, {}),
  disableConnection: (id) => request("POST", `/import-candidates/connections/${id}/disable`, {}),
  disconnectConnection: (id) => request("POST", `/import-candidates/connections/${id}/disconnect`, {}),
  syncConnection: (id, data = {}) => request("POST", `/import-candidates/connections/${id}/sync`, data),
  syncAllConnections: () => request("POST", "/import-candidates/connections/sync-all", {}),
  discoverEmail: (data = {}) => request("POST", "/import-candidates/discover-email", data),
  discoverCalendar: (data = {}) => request("POST", "/import-candidates/discover-calendar", data),
  discoverFile: async (file, data = {}) => {
    const form = new FormData();
    form.append("file", file);
    Object.entries(data || {}).forEach(([key, value]) => {
      if (value !== undefined && value !== null && String(value).trim() !== "") {
        form.append(key, value);
      }
    });
    const token = getAuthToken();
    const headers = { "X-Requested-With": "XMLHttpRequest" };
    if (token) headers["Authorization"] = `Bearer ${token}`;
    const res = await fetch(`${BASE}/import-candidates/discover-file`, { method: "POST", headers, body: form });
    if (res.status === 401) {
      clearAuthTokens();
      window.dispatchEvent(new Event("hm_signout"));
      throw new Error("Session expired. Please sign in again.");
    }
    if (!res.ok) {
      let msg = `HTTP ${res.status}`;
      try {
        const payload = await res.json();
        msg = payload.error || msg;
      } catch {}
      throw new Error(msg);
    }
    return res.json();
  },
  approve:   (id)   => request("POST", `/import-candidates/${id}/approve`, {}),
  approveAndTrust: (id, data = {}) => request("POST", `/import-candidates/${id}/approve-and-trust`, data),
  trustedRules: () => request("GET", "/import-candidates/trusted-rules"),
  disableTrustedRule: (id) => request("DELETE", `/import-candidates/trusted-rules/${id}`),
  mergeAndTrust: (id, data = {}) => request("POST", `/import-candidates/${id}/merge-and-trust`, data),
  trustedUpdates: (limit) => request("GET", `/import-candidates/trusted-updates${limit ? `?limit=${limit}` : ""}`),
  undoTrustedUpdate: (id) => request("POST", `/import-candidates/trusted-updates/${id}/undo`, {}),
  correct:   (id, data = {}) => request("PATCH", `/import-candidates/${id}`, { proposed_payload: data }),
  merge:     (id, data = {}) => request("POST", `/import-candidates/${id}/merge`, data),
  resolveMerge: (id, data = {}) => request("POST", `/import-candidates/${id}/resolve-and-merge`, data),
  dismiss:   (id, data = {}) => request("POST", `/import-candidates/${id}/dismiss`, data),
  approveBatch: (data = {}) => request("POST", "/import-candidates/approve-batch", data),
};

export const casesApi = {
  refreshTripReadiness: () => request("POST", "/cases/refresh-trip-readiness", {}),
  list: (status = "open") => request("GET", `/cases?status=${encodeURIComponent(status)}`),
  summary: () => request("GET", "/cases/summary"),
  get: (id) => request("GET", `/cases/${id}`),
  updateStatus: (id, data) => request("POST", `/cases/${id}/status`, data),
  recordOutcome: (id, data) => request("POST", `/cases/${id}/outcome`, data),
  prepareAction: (caseId, actionId) => request("POST", `/cases/${caseId}/actions/${actionId}/prepare`, {}),
  resolutionProducts: (id) => request("GET", `/cases/${id}/resolution/products`),
  recordResolutionOrder: (id, data) => request("POST", `/cases/${id}/resolution/order`, data),
  updateResolutionStatus: (id, data) => request("POST", `/cases/${id}/resolution/status`, data),
};

export const financialApi = {
  summary: () => request("GET", "/financial/summary"),
  importCsv: async (file, sourceAccount = "") => {
    const form = new FormData();
    form.append("file", file);
    if (sourceAccount) form.append("source_account", sourceAccount);
    const token = getAuthToken();
    const headers = { "X-Requested-With": "XMLHttpRequest" };
    if (token) headers.Authorization = `Bearer ${token}`;
    const res = await fetch(`${BASE}/financial/transactions/import`, { method: "POST", headers, body: form });
    const payload = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(payload.error || `HTTP ${res.status}`);
    return payload;
  },
};

export const householdGraphApi = {
  get: (status = "all") => request("GET", `/household-graph?status=${encodeURIComponent(status)}`),
  sync: () => request("POST", "/household-graph/sync", {}),
};

export const seedApi = {
  demoHousehold: (data = {}) => request("POST", "/seed/demo-household", data),
  onboardingDiscovery: (data = {}) => request("POST", "/seed/onboarding-discovery", data),
};

export const notificationApi = {
  list:     ()   => request("GET",  "/notifications/"),
  feed:     ()   => request("GET",  "/notifications/feed"),
  count:    ()   => request("GET",  "/notifications/count"),
  readAll:  ()   => request("POST", "/notifications/read-all", {}),
  readOne:  (id) => request("POST", `/notifications/${id}/read`, {}),
  dismissOne: (id) => request("POST", `/notifications/${id}/dismiss`, {}),
  clear:    ()   => request("POST", "/notifications/clear", {}),
  pushConfig: () => request("GET", "/notifications/push/config"),
  subscribePush: (data) => request("POST", "/notifications/push/subscribe", data),
  unsubscribePush: (endpoint) => request("POST", "/notifications/push/unsubscribe", { endpoint }),
  testPush: () => request("POST", "/notifications/push/test", {}),
};

export const householdRecordsApi = {
  list:      (type) => request("GET", `/household-records/${type ? `?type=${encodeURIComponent(type)}` : ""}`),
  listVault: ()     => request("GET", "/household-records/?group=vault"),
  add:       (data) => request("POST", "/household-records/", data),
  update:    (id, data) => request("PATCH", `/household-records/${id}`, data),
  completeMaintenance: (id, data = {}) => request("POST", `/household-records/${id}/complete-maintenance`, data),
  remove:    (id) => request("DELETE", `/household-records/${id}`),
  ask:       (id, data) => request("POST", `/household-records/${id}/ask`, data),
  clearAiNotes: (id) => request("DELETE", `/household-records/${id}/ai-notes`),
  askHome:   (data) => request("POST", "/household-records/ask-home", data),
  exportPdf: async () => {
    const token = getAuthToken();
    const headers = { "X-Requested-With": "XMLHttpRequest" };
    if (token) headers["Authorization"] = `Bearer ${token}`;
    const res = await fetch(`${BASE}/household-records/export.pdf`, { headers });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "housemind-home-vault.pdf";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  },
  scan: async (file, docType) => {
    const form = new FormData();
    form.append("file", await prepareScanUpload(file));
    form.append("doc_type", docType);
    const token = getAuthToken();
    const headers = { "X-Requested-With": "XMLHttpRequest" };
    if (token) headers["Authorization"] = `Bearer ${token}`;
    return fetch(`${BASE}/household-records/scan`, { method: "POST", headers, body: form })
      .then(async res => {
        if (res.status === 401) {
          clearAuthTokens();
          window.dispatchEvent(new Event("hm_signout"));
          throw new Error("Session expired. Please sign in again.");
        }
        if (!res.ok) {
          let msg = scanUploadErrorMessage(res);
          try {
            const payload = await res.json();
            msg = payload.message || payload.error || msg;
          } catch {}
          throw new Error(msg);
        }
        return res.json();
      });
  },
};
